import * as RadixRadioGroup from '@radix-ui/react-radio-group';
import { forwardRef, type JSX, type Ref } from 'react';

import { Tooltip } from '~/components/granular';
import { classNames } from '~/utils/styles';
import { Item } from './components';
import type { RadioGroupProps } from './RadioGroup.types';

function RadioGroupComponent<T extends string>(
  {
    className,
    items,
    value,
    defaultValue,
    ariaLabel,
    appearance = 'light',
    alignItems = 'center',
    bordered = false,
    disabled,
    disabledTooltipText,
    onValueChange,
    ...props
  }: RadioGroupProps<T>,
  ref: Ref<HTMLDivElement>,
) {
  const hasDisabledTooltip = !!disabled && !!disabledTooltipText;
  // The tooltip's wrapper is an inline-flex row, where the group would otherwise shrink to its content
  const classes = classNames('flex flex-col', bordered ? 'gap-0' : 'gap-3', hasDisabledTooltip && 'w-full', className);

  const radioGroup = (
    <RadixRadioGroup.Root
      aria-label={ariaLabel}
      className={classes}
      defaultValue={defaultValue}
      disabled={disabled}
      onValueChange={onValueChange}
      orientation="vertical"
      ref={ref}
      value={value}
      {...props}
    >
      {items.map((item, index) => (
        <Item
          alignItems={alignItems}
          appearance={appearance}
          // Every row but the first is pulled up by a pixel so adjacent borders collapse into a
          // single shared line and the stack of options reads as one bordered block.
          className={
            bordered
              ? classNames('border border-solid border-neutral-400 px-3 py-2.5', index > 0 && '-mt-px')
              : undefined
          }
          disabled={item.disabled || disabled}
          id={item.id}
          key={item.id}
          label={item.label}
          value={item.value}
        />
      ))}
    </RadixRadioGroup.Root>
  );

  if (!hasDisabledTooltip) {
    return radioGroup;
  }

  return (
    <Tooltip align="start" trigger={radioGroup} triggerClassName="w-full" triggerDisabled>
      {disabledTooltipText}
    </Tooltip>
  );
}

export const RadioGroup = forwardRef(RadioGroupComponent) as <T extends string = string>(
  props: RadioGroupProps<T> & { ref?: Ref<HTMLDivElement> },
) => JSX.Element;
